import React, { useEffect, useState } from 'react';
import QrCode from '@/components/shared/QrCode';
import AddToWallet from '../../../../assets/order/add-to-wallet.svg';
import PDFIcon from '../../../../assets/order/pdf.svg';
import * as m from "@/paraglide/messages.js"
import { AvailableTicket, TicketType } from '@/lib/OrderApi';
import { RiLock2Fill } from '@remixicon/react';
import { formatInTimeZone } from 'date-fns-tz';
import { parseISO } from 'date-fns';

interface EntranceTicketProps {
  ticket: AvailableTicket;
}

const EntranceTicket: React.FC<EntranceTicketProps> = ({ ticket }) => {
  const isMac = /(iPhone|iPod|iPad)/i.test(navigator.userAgent)
  const [isAppleDevice, setIsAppleDevice] = useState(false);

  // Safety check: Make sure this is an EntranceTicket
  if (ticket.ticketType !== TicketType.EntranceTicket) {
    console.error('Invalid ticket type passed to EntranceTicket component');
    return <div>Invalid ticket type</div>;
  }

  useEffect(() => {
    if (isMac) {
      setIsAppleDevice(true);
    }
  }, [isMac])

  // Handle sealed tickets
  if (ticket.sealed) {
    const releaseDate = ticket.availableFrom ? ticket.availableFrom.split('[')[0] : null;
    const formattedDate = releaseDate 
      ? formatInTimeZone(parseISO(releaseDate), 'Europe/Amsterdam', "MMMM d, yyyy 'at' HH:mm") 
      : 'later';

    return (
      <div className='text-center mx-auto text-[var(--shop-textColor)] py-8 flex flex-col items-center gap-4'>
        <RiLock2Fill className='w-12 h-12 opacity-70'/>
        <h3 className='text-lg font-semibold'>{m.ticket_sealed_title()}</h3>
        <p className='text-sm'>
          {m.ticket_sealed_description_alt()}
          <br />
          <span className="font-semibold">{formattedDate}</span>
        </p>
      </div>
    );
  }

  // Render QR code and download buttons for available tickets
  return (
    <>
      <div className='text-center mx-auto text-[var(--shop-textColor)]'>
        {ticket.noMoreValidEntrances && (
          <>
            <span className='block text-red-600 font-bold text-lg mb-1'>
              USED
            </span>
            {ticket.firstScannedAt && (
              <p className='text-xs mb-2'>
                Scanned on {formatInTimeZone(
                  parseISO(ticket.firstScannedAt.split('[')[0]),
                  'Europe/Amsterdam', 
                  "MMMM d, yyyy 'at' HH:mm"
                )}
              </p>
            )}
          </>
        )}
        {ticket.barcode}
      </div>
      <QrCode
        size={256}
        className='mx-auto max-w-[200px] p-4 bg-white my-6'
        value={ticket.barcode}
      />

      {isAppleDevice && ticket.wallet_download_url && (
        <a href={ticket.wallet_download_url} target="_blank" rel="noopener noreferrer">
          <img src={AddToWallet} alt="Add to Apple Wallet" className='h-16 py-2 mx-auto cursor-pointer hover:opacity-80 transition-opacity'/>
        </a>
      )}

      {ticket.pdf_download_url && (
        <a href={ticket.pdf_download_url} className='bg-[var(--shop-ctaButtonColor)] text-[var(--shop-ctaButtonTextColor)] font-bold p-2 rounded-md text-center block w-full hover:opacity-90 transition-all duration-300 hover:shadow-md mt-2' target="_blank" rel="noopener noreferrer">
          {m.order_available_ticket_modal_download_ticket()} <img src={PDFIcon} alt="" className='ml-2 h-4 inline-block'/>
        </a>
      )}
    </>
  );
};

export default EntranceTicket; 